<template>
  <FloatingPanel
    :show="show"
    :title="`测量 - ${projectName}`"
    :icon="measureToolIcon"
    :width="panelWidth"
    :estimated-height="panelEstimatedHeight"
    :collapsed="collapsed"
    :show-collapse="showCollapse"
    :show-help="showHelp"
    :draggable="draggable"
    :reset-position-on-show="resetPositionOnShow"
    placement="top-left"
    :z-index="zIndex"
    @update:show="handleShowUpdate"
    @update:collapsed="handleCollapsedUpdate"
    @close="emit('close')"
    @help="emit('help')"
  >
    <div
      class="measure-dialog-toolbar"
      @pointerdown="emit('panel-interact')"
    >
      <template
        v-for="action in measureToolActions"
        :key="action.key"
      >
        <!-- 圆弧/圆测量（带右侧下拉） -->
        <DropdownButton
          v-if="action.key === 'arc-circle'"
          :is-active="isArcCircleActive"
          :disabled="Boolean(activeArcCircleOption.disabled)"
          :icon="activeArcCircleOption.icon"
          :label="activeArcCircleOption.label"
          icon-only
          size="small"
          direction="row"
          :options="arcCircleDropdownOptions"
          @select="handleArcCircleSelect"
          @click="handleArcCircleClick"
        />

        <!-- 投影于（带右侧下拉） -->
        <DropdownButton
          v-else-if="action.key === 'projected-on'"
          :is-active="isProjectedOnActive"
          :disabled="Boolean(activeProjectedOnOption.disabled)"
          :icon="activeProjectedOnOption.icon"
          :label="activeProjectedOnOption.label"
          icon-only
          size="small"
          direction="row"
          :options="projectedOnDropdownOptions"
          @select="handleProjectedOnSelect"
          @click="handleProjectedOnClick"
        />

        <!-- 其他普通按钮（无下拉）；显示 XYZ 按激活态展示 -->
        <DropdownButton
          v-else
          :is-active="resolvePlainToolActive(action)"
          :disabled="Boolean(action.disabled)"
          :icon="action.icon"
          :label="action.label"
          icon-only
          size="small"
          direction="row"
          @click="handlePlainToolClick(action)"
        />
      </template>
    </div>

    <MeasureResultPanel
      v-if="show"
      :single-view-state="singleViewState"
      :pair-callouts="resultPairCallouts"
      :line-visibility-group="lineVisibilityGroup"
      @select-item="handleResultItemSelect"
      @hover-item="handleResultItemHoverEnter"
      @hover-leave="handleResultItemHoverLeave"
      @pointerdown="emit('panel-interact')"
    />
  </FloatingPanel>
</template>

<script setup lang="ts">
import { computed, h, ref, toRef, watch } from 'vue';
import type { DropdownOption } from 'naive-ui';
import type { MeasurePairMeasurementCalloutItem } from '@puppycad/cad-core';
import DropdownButton from '@/components/common/DropdownButton.vue';
import SvgIcon from '@/components/common/SvgIcon.vue';
import FloatingPanel from '@/components/editor/FloatingPanel.vue';
import MeasureResultPanel from '@/components/editor/evalution/measure/MeasureResultPanel.vue';
import { useMeasureResultPanel } from '@/composables/measure/useMeasureResultPanel';
import { useViewer } from '@/hooks/useViewer';
import measureToolIcon from '@/assets/icon/evalution_measurement.svg?raw';
import EvalutionMeasureMinimumDistanceIcon from '@/assets/icon/evalution_measure_minimum_distance.svg?raw';
import EvalutionMeasureCenterToCenterIcon from '@/assets/icon/evalution_measure_center_to_center.svg?raw';
import EvalutionMeasureMaximumDistanceIcon from '@/assets/icon/evalution_measure_maximum_distance.svg?raw';
import EvalutionMeasureCustomDistanceIcon from '@/assets/icon/evalution_measure_custom_distance.svg?raw';
import EvalutionMeasureUnitsPrecisionIcon from '@/assets/icon/evalution_measure_units_precision.svg?raw';
import EvalutionMeasureDisplayXyzIcon from '@/assets/icon/evalution_measure_display_xyz.svg?raw';
import EvalutionMeasurePointToPointIcon from '@/assets/icon/evalution_measure_point_to_point.svg?raw';
import EvalutionMeasureProjectedOnIcon from '@/assets/icon/evalution_measure_projected_on.svg?raw';
import EvalutionMeasureScreenIcon from '@/assets/icon/evalution_measure_screen.svg?raw';
import EvalutionMeasureReferencePlaneIcon from '@/assets/icon/evalution_measure_reference_plane.svg?raw';
import EvalutionMeasureMeasurementHistoryIcon from '@/assets/icon/evalution_measure_measurement_history.svg?raw';
import EvalutionMeasureCreateSensorIcon from '@/assets/icon/evalution_measure_create_sensor.svg?raw';


/** 普通仅图标按钮占位宽度（px）。 */
const MEASURE_TOOL_BUTTON_WIDTH = 40;

/** 带下拉箭头按钮占位宽度（px）。 */
const MEASURE_TOOL_DROPDOWN_BUTTON_WIDTH = 52;

/** 测量工具栏水平内边距（px）。 */
const MEASURE_TOOLBAR_HORIZONTAL_PADDING = 16;

/** 测量工具栏预估高度（px），用于浮动面板边界钳制。 */
const MEASURE_TOOLBAR_ESTIMATED_HEIGHT = 88;

/** 结果区预估高度（px）。 */
const MEASURE_RESULT_ESTIMATED_HEIGHT = 180;

/** 带下拉的测量工具键集合。 */
const MEASURE_DROPDOWN_TOOL_KEYS = new Set<string>([
  'arc-circle',
  'projected-on',
]);

/**
 * 工具栏 action 配置项。
 */
interface ToolbarAction {
  /** 工具唯一键。 */
  key: string;
  /** 按钮标签。 */
  label: string;
  /** 按钮图标。 */
  icon: string;
  /** 关联命令。 */
  command?: string;
  /** 操作提示。 */
  hint?: string;
  /** 是否禁用。 */
  disabled?: boolean;
}

/** 圆弧/圆测量模式键。 */
type MeasureArcCircleModeKey = 'center-to-center' | 'minimum-distance' | 'maximum-distance' | 'custom-distance';

/** 投影于模式键。 */
type MeasureProjectedOnModeKey = 'none' | 'screen' | 'reference plane';

/**
 * 圆弧/圆测量下拉选项。
 */
interface MeasureArcCircleModeOption {
  /** 模式键。 */
  mode: MeasureArcCircleModeKey;
  /** 选项标签。 */
  label: string;
  /** 选项图标。 */
  icon: string;
  /** 关联命令。 */
  command: string;
  /** 操作提示。 */
  hint: string;
  /** 是否禁用。 */
  disabled?: boolean;
}

/**
 * 投影于下拉选项。
 */
interface MeasureProjectedOnModeOption {
  /** 模式键。 */
  mode: MeasureProjectedOnModeKey;
  /** 选项标签。 */
  label: string;
  /** 选项图标。 */
  icon: string;
  /** 关联命令。 */
  command: string;
  /** 操作提示。 */
  hint: string;
  /** 是否禁用。 */
  disabled?: boolean;
}

/**
 * 测量对话框属性。
 */
interface MeasureDialogProps {
  /** 对话框是否可见。 */
  show: boolean;
  /** 当前激活的测量工具键。 */
  activeTool?: string | null;
  /** 是否折叠内容区。 */
  collapsed?: boolean;
  /** 是否显示折叠按钮。 */
  showCollapse?: boolean;
  /** 是否显示帮助按钮。 */
  showHelp?: boolean;
  /** 标题栏是否可拖动。 */
  draggable?: boolean;
  /** 每次重新显示时是否重置位置。 */
  resetPositionOnShow?: boolean;
  /** 视口层 z-index。 */
  zIndex?: number;
  /** 项目标题。 */
  projectName?: string;
}

const props = withDefaults(defineProps<MeasureDialogProps>(), {
  activeTool: null,
  collapsed: false,
  showCollapse: true,
  showHelp: true,
  draggable: true,
  resetPositionOnShow: true,
  zIndex: 4000,
  projectName: '',
});

const emit = defineEmits<{
  /** 关闭测量对话框。 */
  close: [];
  /** 点击帮助。 */
  help: [];
  /** 点击测量工具按钮。 */
  'tool-click': [toolKey: string];
  /** 用户与测量面板交互（用于恢复拾取）。 */
  'panel-interact': [];
  /** 同步可见状态。 */
  'update:show': [show: boolean];
  /** 同步激活工具。 */
  'update:activeTool': [toolKey: string | null];
  /** 同步折叠状态。 */
  'update:collapsed': [collapsed: boolean];
}>();

/**
 * 测量工具栏按钮配置。
 *
 * 设计说明：顺序与 SolidWorks 测量对话框工具栏一致，图标暂统一占位。
 */
const measureToolActions: ToolbarAction[] = [
  {
    key: 'arc-circle',
    label: '圆弧/圆测量',
    icon: measureToolIcon,
    command: 'arc-circle',
    hint: '圆弧/圆测量',
  },
  {
    key: 'units-precision',
    label: '单位/精度',
    icon: EvalutionMeasureUnitsPrecisionIcon,
    command: 'units-precision',
    hint: '单位/精度',
  },
  {
    key: 'display-xyz',
    label: '显示XYZ测量',
    icon: EvalutionMeasureDisplayXyzIcon,
    command: 'display-xyz',
    hint: '显示XYZ测量',
  },
  {
    key: 'point-to-point',
    label: '点到点测量',
    icon: EvalutionMeasurePointToPointIcon,
    command: 'point-to-point',
    hint: '点到点测量',
  },
  {
    key: 'projected-on',
    label: '投影于',
    icon: EvalutionMeasureProjectedOnIcon,
    command: 'projected-on',
    hint: '投影于',
  },
  {
    key: 'measurement-history',
    label: '测量历史记录',
    icon: EvalutionMeasureMeasurementHistoryIcon,
    command: 'measurement-history',
    hint: '测量历史记录',
  },
  {
    key: 'create-sensor',
    label: '创建传感器',
    icon: EvalutionMeasureCreateSensorIcon,
    command: 'create-sensor',
    hint: '创建基准面',
  },
];

/**
 * 圆弧/圆测量下拉选项。
 */
const measureArcCircleModeOptions: MeasureArcCircleModeOption[] = [
  {
    mode: 'center-to-center',
    label: '中心到中心',
    icon: EvalutionMeasureCenterToCenterIcon,
    command: 'center-to-center',
    hint: '中心到中心',
  },
  {
    mode: 'minimum-distance',
    label: '最小距离',
    icon: EvalutionMeasureMinimumDistanceIcon,
    command: 'minimum-distance',
    hint: '最小距离',
  },
  {
    mode: 'maximum-distance',
    label: '最大距离',
    icon: EvalutionMeasureMaximumDistanceIcon,
    command: 'maximum-distance',
    hint: '最大距离',
  },
  {
    mode: 'custom-distance',
    label: '自定义距离',
    icon: EvalutionMeasureCustomDistanceIcon,
    command: 'custom-distance',
    hint: '自定义距离',
  },
];

/**
 * 投影于下拉选项。
 */
const measureProjectedOnModeOptions: MeasureProjectedOnModeOption[] = [
  {
    mode: 'none',
    label: '无',
    icon: EvalutionMeasureProjectedOnIcon,
    command: 'none',
    hint: '无',
  },
  {
    mode: 'screen',
    label: '屏幕',
    icon: EvalutionMeasureScreenIcon,
    command: 'screen',
    hint: '屏幕',
  },
  {
    mode: 'reference plane',
    label: '选择面/基准面',
    icon: EvalutionMeasureReferencePlaneIcon,
    command: 'reference plane',
    hint: '选择面/基准面',
  },
];

/** 下拉菜单 option 图标尺寸；使用 inline style 避免 teleport 后 scoped 样式失效。 */
const dropdownOptionIconStyle = { width: '18px', height: '18px', objectFit: 'contain' } as const;

/**
 * 构建工具栏下拉选项。
 *
 * @param option 下拉选项配置。
 * @returns Naive UI DropdownOption。
 */
function buildToolbarDropdownOption(option: { command: string; label: string; icon?: string }): DropdownOption {
  const dropdownOption: DropdownOption = {
    key: option.command,
    label: option.label,
  };
  if (option.icon) {
    dropdownOption.icon = () => h(SvgIcon, {
      content: option.icon,
      alt: option.label,
      class: 'dropdown-option-icon',
      style: dropdownOptionIconStyle,
    });
  }
  return dropdownOption;
}

/** 圆弧/圆测量模式索引。 */
const arcCircleModeMap = new Map(measureArcCircleModeOptions.map((option) => [option.mode, option]));
const arcCircleCommandMap = new Map(measureArcCircleModeOptions.map((option) => [option.command, option]));
/** 进入测量时的默认圆弧/圆子模式。 */
const DEFAULT_ARC_CIRCLE_MODE: MeasureArcCircleModeKey = 'minimum-distance';

/** 当前圆弧/圆测量模式。 */
const activeArcCircleMode = ref<MeasureArcCircleModeKey>(DEFAULT_ARC_CIRCLE_MODE);

/** 当前圆弧/圆测量选项。 */
const activeArcCircleOption = computed(() => (
  arcCircleModeMap.get(activeArcCircleMode.value) ?? measureArcCircleModeOptions[0]
));
/** 圆弧/圆测量是否激活。 */
const isArcCircleActive = computed(() => props.activeTool === 'arc-circle');
/** 圆弧/圆测量下拉选项。 */
const arcCircleDropdownOptions = computed<DropdownOption[]>(() => (
  measureArcCircleModeOptions.map((option) => buildToolbarDropdownOption(option))
));

/** 投影于模式索引。 */
const projectedOnModeMap = new Map(measureProjectedOnModeOptions.map((option) => [option.mode, option]));
const projectedOnCommandMap = new Map(measureProjectedOnModeOptions.map((option) => [option.command, option]));
/** 当前投影于模式。 */
const activeProjectedOnMode = ref<MeasureProjectedOnModeKey>('none');
/** 当前投影于选项。 */
const activeProjectedOnOption = computed(() => (
  projectedOnModeMap.get(activeProjectedOnMode.value) ?? measureProjectedOnModeOptions[0]
));
/** 投影于是否激活。 */
const isProjectedOnActive = computed(() => props.activeTool === 'projected-on');
/** 投影于下拉选项。 */
const projectedOnDropdownOptions = computed<DropdownOption[]>(() => (
  measureProjectedOnModeOptions.map((option) => buildToolbarDropdownOption(option))
));

/**
 * 判断测量工具是否带下拉。
 *
 * @param toolKey 工具键。
 * @returns 是否带下拉。
 */
function isMeasureDropdownTool(toolKey: string): boolean {
  return MEASURE_DROPDOWN_TOOL_KEYS.has(toolKey);
}

/**
 * 浮动面板宽度：按按钮类型累加占位宽度。
 */
const panelWidth = computed<number>(() => {
  const actionsWidth = measureToolActions.reduce((total, action) => (
    total + (isMeasureDropdownTool(action.key)
      ? MEASURE_TOOL_DROPDOWN_BUTTON_WIDTH
      : MEASURE_TOOL_BUTTON_WIDTH)
  ), 0);
  return (actionsWidth + MEASURE_TOOLBAR_HORIZONTAL_PADDING) * 1.5;
});

/** 浮动面板预估高度。 */
const panelEstimatedHeight = computed(() => (
  MEASURE_TOOLBAR_ESTIMATED_HEIGHT + MEASURE_RESULT_ESTIMATED_HEIGHT
));

const { getViewer } = useViewer();
/** 测量会话激活态与对话框可见性绑定。 */
const isMeasureActive = toRef(props, 'show');
/** 结果区状态。 */
const {
  singleViewState,
  pairMeasurement,
  lineVisibilityGroup,
  handleResultItemSelect: applyResultItemSelect,
  handleResultItemHoverEnter: applyResultItemHoverEnter,
  handleResultItemHoverLeave: applyResultItemHoverLeave,
} = useMeasureResultPanel({
  getViewer,
  isMeasureActive,
});
/** 双选结果项。 */
const resultPairCallouts = computed(() => (
  pairMeasurement.value?.callouts ?? []
));
/** 「显示 XYZ」工具栏按钮激活态（与 cad-core displayPolicy 同步）。 */
const isDisplayXyzEnabled = ref(true);

/** 对话框重新打开时重置圆弧/圆子模式，并同步显示 XYZ 状态。 */
watch(() => props.show, (visible) => {
  if (visible) {
    activeArcCircleMode.value = DEFAULT_ARC_CIRCLE_MODE;
    isDisplayXyzEnabled.value = getViewer()?.measureController.isDxDyDzMeasurementEnabled() ?? true;
  }
});

/**
 * 激活测量工具并向上抛出事件。
 *
 * @param toolKey 父级工具键。
 * @param command 实际命令（含下拉子项）。
 */
function activateMeasureTool(toolKey: string, command: string): void {
  emit('update:activeTool', toolKey);
  emit('tool-click', command);
}

/**
 * 解析普通工具按钮是否高亮。
 *
 * @param action 工具栏 action。
 * @returns 是否激活。
 */
function resolvePlainToolActive(action: ToolbarAction): boolean {
  if (action.command === 'display-xyz') {
    return isDisplayXyzEnabled.value;
  }
  return props.activeTool === action.command;
}

/**
 * 处理结果区双选项点选。
 *
 * @param item 结果项。
 */
function handleResultItemSelect(item: MeasurePairMeasurementCalloutItem): void {
  emit('panel-interact');
  applyResultItemSelect(item);
}

/**
 * 处理结果区双选项 hover 进入。
 *
 * @param item 结果项。
 */
function handleResultItemHoverEnter(item: MeasurePairMeasurementCalloutItem): void {
  applyResultItemHoverEnter(item);
}

/**
 * 处理结果区双选项 hover 离开。
 */
function handleResultItemHoverLeave(): void {
  applyResultItemHoverLeave();
}

/**
 * 应用圆弧/圆测量命令。
 *
 * @param command 下拉命令键。
 */
function applyArcCircleCommand(command: string): void {
  const option = arcCircleCommandMap.get(command);
  if (!option || option.disabled) {
    return;
  }
  activeArcCircleMode.value = option.mode;
  activateMeasureTool('arc-circle', option.command);
}

/**
 * 应用投影于命令。
 *
 * @param command 下拉命令键。
 */
function applyProjectedOnCommand(command: string): void {
  const option = projectedOnCommandMap.get(command);
  if (!option || option.disabled) {
    return;
  }
  activeProjectedOnMode.value = option.mode;
  activateMeasureTool('projected-on', option.command);
}

/**
 * 处理圆弧/圆测量下拉选择。
 *
 * @param key 下拉命令键。
 */
function handleArcCircleSelect(key: string): void {
  applyArcCircleCommand(key);
}

/**
 * 处理圆弧/圆测量主按钮点击。
 */
function handleArcCircleClick(): void {
  applyArcCircleCommand(activeArcCircleOption.value.command);
}

/**
 * 处理投影于下拉选择。
 *
 * @param key 下拉命令键。
 */
function handleProjectedOnSelect(key: string): void {
  applyProjectedOnCommand(key);
}

/**
 * 处理投影于主按钮点击。
 */
function handleProjectedOnClick(): void {
  applyProjectedOnCommand(activeProjectedOnOption.value.command);
}

/**
 * 处理普通测量工具点击。
 *
 * @param action 工具栏 action。
 */
function handlePlainToolClick(action: ToolbarAction): void {
  if (action.disabled) {
    return;
  }
  const command = action.command ?? action.key;
  // 显示 XYZ：本地乐观切换，与 cad-core toggle 保持同一拍
  if (command === 'display-xyz') {
    isDisplayXyzEnabled.value = !isDisplayXyzEnabled.value;
  }
  activateMeasureTool(command, command);
}

/**
 * 同步可见状态。
 *
 * @param visible 是否可见。
 */
function handleShowUpdate(visible: boolean): void {
  emit('update:show', visible);
}

/**
 * 同步折叠状态。
 *
 * @param collapsed 是否折叠。
 */
function handleCollapsedUpdate(collapsed: boolean): void {
  emit('update:collapsed', collapsed);
}
</script>

<style scoped lang="less">
.measure-dialog-toolbar {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 40px;
}

:deep(.dropdown-option-icon) {
  width: 18px;
  height: 18px;
  object-fit: contain;
}
</style>
